// src/store/slices/subCategoriesSlice.ts
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import apiClient from '@/libs/apiClient'
import type { Subcategory } from '@/types/catalog'
import type { RootState } from '@/redux-store'

// ---------------------------
// API Types
// ---------------------------
export interface ApiSubcategory {
  id: string
  numeric_id?: number
  title: string
  title_en: string
  title_ar: string
  description: string
  description_en: string
  description_ar: string
  products_count: number
  image?: {
    url: string
    type: string
    document_type: string
    disk: string
    created_at: string
  }
  is_enabled: boolean
  category?: {
    id: string
    title_en: string
    title_ar: string
  }
  created_at: string
  updated_at: string
}

export const mapApiToSubcategory = (api: ApiSubcategory): Subcategory => ({
  id: api.id,
  numeric_id: api.numeric_id || 0,
  name: api.title_en,
  nameAr: api.title_ar,

  // title_en can come back as a bare number if someone typed digits-only into
  // the title field (the DB column is a string, but JSON encodes an
  // all-numeric string as a number) — String() first so this never throws.
  slug: String(api.title_en ?? '').toLowerCase().replace(/ /g, '-'),
  image: api.image,
  description: api.description_en,
  description_ar: api.description_ar,
  categoryId: api.category?.id || '',
  categoryName: api.category?.title_en || '',
  categoryNameAr: api.category?.title_ar || '',
  order: 0,
  isActive: Boolean(api.is_enabled),
  isArchived: false,
  productsCount: api.products_count || 0
})

// ---------------------------
// Pagination & State
// ---------------------------
interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

// Cache key: categoryId or '__all__' for no filter
type CacheKey = string

interface CachedPage {
  subcategories: Subcategory[]
  pagination: Pagination | null
  fetchedAt: number
}

interface SubcategoryState {
  subcategories: Subcategory[]
  pagination: Pagination | null
  loading: boolean
  error: string | null
  cache: Record<CacheKey, CachedPage>
  currentKey: CacheKey
}

const CACHE_TTL = 60_000 // 60 seconds

const initialState: SubcategoryState = {
  subcategories: [],
  pagination: null,
  loading: false,
  error: null,
  cache: {},
  currentKey: '__all__'
}

// ---------------------------
// Async Thunks
// ---------------------------

// Fetch Subcategories (with per-category cache)
export const fetchSubcategories = createAsyncThunk<
  { subcategories: Subcategory[]; pagination: Pagination; cacheKey: CacheKey },
  { categoryId?: string; limit?: number; forceRefresh?: boolean },
  { rejectValue: string; state: RootState }
>('subcategories/fetchSubcategories', async ({ categoryId, limit = 100, forceRefresh }, { rejectWithValue, getState }) => {
  const cacheKey: CacheKey = categoryId || '__all__'

  // Return cached data if fresh enough
  if (!forceRefresh) {
    const cached = getState().subCategories.cache[cacheKey]

    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      return { subcategories: cached.subcategories, pagination: cached.pagination!, cacheKey }
    }
  }

  try {
    const targetUrl = (categoryId && categoryId !== 'undefined' && categoryId !== 'null')
      ? `/admin/v1/categories/${categoryId}/sub-categories`
      : '/admin/v1/sub-categories'

    const res = await apiClient.get(targetUrl, { params: { limit } })
    const json = res.data

    const subcategories: Subcategory[] = json.data.map((sc: ApiSubcategory) => mapApiToSubcategory(sc))

    const pagination: Pagination = {
      currentPage: json.meta.currentPage,
      lastPage: json.meta.lastPage,
      perPage: json.meta.perPage,
      total: json.meta.total
    }

    return { subcategories, pagination, cacheKey }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch subcategories')
  }
})

// Add Subcategory
export const addSubcategory = createAsyncThunk<
  ApiSubcategory,
  { categoryId?: string; data: FormData },
  { rejectValue: string }
>('subcategories/addSubcategory', async ({ categoryId, data }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post(`/admin/v1/categories/${categoryId}/sub-categories/add`, data)
    const json = res.data

    return json.data as ApiSubcategory
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add subcategory')
  }
})

// Edit Subcategory
export const editSubcategory = createAsyncThunk<
  ApiSubcategory,
  { subcategoryId: string; data: FormData },
  { rejectValue: string }
>('subcategories/edit', async ({ subcategoryId, data }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post(`/admin/v1/sub-categories/${subcategoryId}/edit`, data)
    const json = res.data

    return json.data as ApiSubcategory
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to edit subcategory')
  }
})

// Delete Subcategory
export const deleteSubcategory = createAsyncThunk<string, string, { rejectValue: string }>(
  'subcategories/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/sub-categories/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete subcategory')
    }
  }
)

// Toggle Subcategory Status
export const toggleSubcategoryStatus = createAsyncThunk<string, { id: string; status: string }, { rejectValue: string }>(
  'subcategories/toggleStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const is_enabled = status === 'enable'

      await apiClient.post(`/admin/v1/sub-categories/${id}/toggle`, { is_enabled })

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to toggle status')
    }
  }
)

// ---------------------------
// Slice
// ---------------------------
const subcategorySlice = createSlice({
  name: 'subcategories',
  initialState,
  reducers: {
    clearSubcategories(state) {
      state.subcategories = []
      state.pagination = null
      state.error = null
      state.cache = {}
    },
    invalidateCache(state, action: PayloadAction<CacheKey | undefined>) {
      if (action.payload) {
        delete state.cache[action.payload]
      } else {
        state.cache = {}
      }
    }
  },
  extraReducers: builder => {
    builder

      // Fetch
      .addCase(fetchSubcategories.pending, (state, action) => {
        const cacheKey = action.meta.arg.categoryId || '__all__'

        // Only show loading spinner if we have no cached data for this key
        if (!state.cache[cacheKey]) {
          state.loading = true
        }

        state.currentKey = cacheKey
        state.error = null
      })
      .addCase(fetchSubcategories.fulfilled, (state, action) => {
        const { subcategories, pagination, cacheKey } = action.payload

        // Always cache the response, but only replace the visible list if this
        // response is for the filter that's still selected — an older, slower
        // request for a category the user has since filtered away from could
        // otherwise resolve after a newer one and silently overwrite it with
        // stale data (a real risk under production network latency/jitter).
        state.cache[cacheKey] = { subcategories, pagination, fetchedAt: Date.now() }

        if (cacheKey !== state.currentKey) return

        state.loading = false
        state.subcategories = subcategories
        state.pagination = pagination
      })
      .addCase(fetchSubcategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch subcategories'
      })

      // Add
      .addCase(addSubcategory.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addSubcategory.fulfilled, (state, action: PayloadAction<ApiSubcategory>) => {
        state.loading = false
        const mapped = mapApiToSubcategory(action.payload)

        state.subcategories.push(mapped)
        if (state.pagination) state.pagination.total += 1

        // Invalidate cache so next tab switch re-fetches
        state.cache = {}
      })
      .addCase(addSubcategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to add subcategory'
      })

      // Edit
      .addCase(editSubcategory.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(editSubcategory.fulfilled, (state, action: PayloadAction<ApiSubcategory>) => {
        state.loading = false
        const updated = mapApiToSubcategory(action.payload)

        state.subcategories = state.subcategories.map(sc => (sc.id === updated.id ? updated : sc))

        // Invalidate cache so next tab switch re-fetches
        state.cache = {}
      })
      .addCase(editSubcategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to edit subcategory'
      })

      // Delete
      .addCase(deleteSubcategory.pending, state => {
        state.loading = true
      })
      .addCase(deleteSubcategory.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false
        state.subcategories = state.subcategories.filter(sc => sc.id !== action.payload)

        // Toasting per-item here would spam a bulk delete (one per row) on
        // top of the consolidated, accurately-counted toast the view already
        // shows once every delete in the batch has settled.
        state.cache = {}
      })
      .addCase(deleteSubcategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete subcategory'
      })

      // Toggle Status
      .addCase(toggleSubcategoryStatus.fulfilled, (state, action: PayloadAction<string>) => {
        state.subcategories = state.subcategories.map(sc => (sc.id === action.payload ? { ...sc, isActive: !sc.isActive } : sc))
        toast.success('Status updated')
        state.cache = {}
      })
  }
})

export const { clearSubcategories, invalidateCache } = subcategorySlice.actions
export default subcategorySlice.reducer
